import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Sun, Moon, ArrowUpRight, Phone, Mail } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { CONTACT } from "@/data/site";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/case-studies", label: "Case Studies", testid: "nav-case-studies-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const Logo = ({ className }) => {
  const { dark } = useTheme();
  return (
    <img
      src={dark ? "/logo-dark.png" : "/logo-light.png"}
      alt="codeRealm World Services LLP"
      className={className || "h-9 w-auto sm:h-10"}
      data-testid="brand-logo"
    />
  );
};

const ThemeToggle = () => {
  const { dark, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="theme-toggle-button"
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      className="group flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors duration-300 hover:border-coral hover:text-coral"
    >
      <motion.span
        key={dark ? "moon" : "sun"}
        initial={{ rotate: -90, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="flex"
      >
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </motion.span>
    </button>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-20 sm:px-8">
        <Link to="/" aria-label="codeRealm home" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 hover:text-coral ${
                  isActive ? "text-coral" : "text-muted-foreground"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="group hidden items-center gap-2 rounded-full bg-primary px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-primary-foreground transition-colors duration-300 hover:bg-coral sm:flex"
          >
            Start a project
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Open menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border md:hidden"
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-background"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <Logo className="h-9 w-auto" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-button"
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <button
                    type="button"
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    onClick={() => {
                      setOpen(false);
                      navigate(item.to);
                    }}
                    className="font-display text-4xl font-bold tracking-tight transition-colors duration-300 hover:text-coral"
                  >
                    {item.label}
                  </button>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {CONTACT.email}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-border">
    <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
      <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <Logo className="h-10 w-auto" />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-muted-foreground">
            AI development, n8n automation and React full-stack engineering —
            built by codeRealm World Services LLP.
          </p>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground/70">
            formerly: Soumitra Mukherjee IT services LLP
          </p>
        </div>
        <nav aria-label="Footer" className="flex flex-col gap-3">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-coral">Explore</span>
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`footer-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className="text-sm text-muted-foreground transition-colors duration-300 hover:text-foreground"
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex flex-col gap-3">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-coral">Contact</span>
          <a
            href={`tel:${CONTACT.phoneHref}`}
            data-testid="footer-phone-link"
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors duration-300 hover:text-foreground"
          >
            <Phone className="h-3.5 w-3.5" /> {CONTACT.phone}
          </a>
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="footer-email-link"
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors duration-300 hover:text-foreground"
          >
            <Mail className="h-3.5 w-3.5" /> {CONTACT.email}
          </a>
        </div>
      </div>
      <div className="mt-14 flex flex-col gap-2 border-t border-border pt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70 sm:flex-row sm:justify-between">
        <span>© 2026 codeRealm World Services LLP</span>
        <span>coderealm.codes</span>
      </div>
    </div>
  </footer>
);

export default function Layout() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-16 sm:pt-20">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
